import { useEffect, useState } from 'react';
import { Drawer, Form, Input, InputNumber, Button, Space, Select, Switch, Divider, message, Tabs } from 'antd';
import { api } from '../api';
import PropertyEditor from './PropertyEditor';
import MediaEditor from './MediaEditor';
import TranslationsEditor from './TranslationsEditor';

export default function CategoryForm({ category, storeId, meta, onSave, onClose }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [properties, setProperties] = useState([]);
  const [componentIds, setComponentIds] = useState([]);
  const [addonIds, setAddonIds] = useState([]);
  const [translations, setTranslations] = useState([]);
  const [availableComponents, setAvailableComponents] = useState([]);
  const [availableAddons, setAvailableAddons] = useState([]);

  const isNew = !category?.id;
  const currentName = Form.useWatch('name', form);

  useEffect(() => {
    api.products.list(storeId, 'component').then(setAvailableComponents).catch(() => {});
    api.products.list(storeId, 'addon').then(setAvailableAddons).catch(() => {});
  }, [storeId]);

  useEffect(() => {
    if (category) {
      form.setFieldsValue({ name: category.name, pos: category.pos ?? 0, is_listed: category.is_listed ?? 0 });
      setProperties(category.properties || []);
      setComponentIds((category.components || []).map(c => c.id));
      setAddonIds((category.addons || []).map(a => a.id));
      setTranslations(category.translations || []);
    } else {
      form.resetFields();
      setProperties([]);
      setComponentIds([]);
      setAddonIds([]);
      setTranslations([]);
    }
  }, [category, form]);

  async function handleSave() {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        ...values,
        is_listed:         values.is_listed ? 1 : 0,
        fk_store:          storeId,
        properties:        properties.map(p => ({ fk_property: p.fk_property, value: p.value })),
        component_ids:     componentIds,
        addon_product_ids: addonIds,
        translations,
      };
      const saved = isNew
        ? await api.categories.create(payload)
        : await api.categories.update(category.id, payload);
      message.success('Gespeichert');
      onSave(saved);
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  }

  const tabs = [
    {
      key: 'main',
      label: 'Stammdaten',
      children: (
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Pflichtfeld' }]}>
            <Input />
          </Form.Item>
          <Space style={{ width: '100%' }} align="start">
            <Form.Item name="pos" label="Position" initialValue={0} style={{ width: 120 }}>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="is_listed" label="Im Shop gelistet" valuePropName="checked" style={{ paddingTop: 4 }}>
              <Switch />
            </Form.Item>
          </Space>
          <Divider />
          <Form.Item label="Komponenten">
            <Select
              mode="multiple"
              value={componentIds}
              onChange={setComponentIds}
              options={availableComponents.map(p => ({ value: p.id, label: p.name || p.id }))}
              placeholder="Komponenten zuordnen…"
              showSearch
              filterOption={(input, opt) => (opt?.label || '').toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>
          <Form.Item label="Addons">
            <Select
              mode="multiple"
              value={addonIds}
              onChange={setAddonIds}
              options={availableAddons.map(p => ({ value: p.id, label: p.name || p.id }))}
              placeholder="Addons zuordnen…"
              showSearch
              filterOption={(input, opt) => (opt?.label || '').toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>
        </Form>
      ),
    },
    {
      key: 'properties',
      label: 'Eigenschaften',
      children: (
        <PropertyEditor
          properties={properties}
          allProperties={meta.properties}
          onChange={setProperties}
        />
      ),
    },
    {
      key: 'translations',
      label: 'Texte',
      children: (
        <TranslationsEditor translations={translations} onChange={setTranslations} defaultName={currentName || ''} />
      ),
    },
    {
      key: 'media',
      label: 'Medien',
      children: (
        <MediaEditor
          entityId={category?.id}
          apiMedia={api.categoryMedia}
          placeholder="Bitte zuerst die Kategorie speichern."
        />
      ),
    },
  ];

  return (
    <Drawer
      title={isNew ? 'Neue Kategorie' : (category?.name || 'Kategorie bearbeiten')}
      open onClose={onClose} width={560}
      footer={
        <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button type="primary" onClick={handleSave} loading={saving}>Speichern</Button>
        </Space>
      }
    >
      <Tabs items={tabs} />
    </Drawer>
  );
}
